import { Bar, Box, Cyl, Lathe, RBox, Ring, Slab } from '../../render/parts'
import type { Tone } from '../../render/theme'

/** Small dressing props. They are what turn a bare desk into a scene. */

export interface BookStackProps {
  count?: number
  width?: number
  depth?: number
  accent?: string
  base?: number
  seed?: number
}

export function BookStack({ count = 3, width = 0.2, depth = 0.15, accent, base = 0, seed = 2 }: BookStackProps) {
  let y = base
  return (
    <group>
      {Array.from({ length: count }, (_, index) => {
        const thickness = 0.022 + ((index * 7 + seed) % 5) * 0.004
        const skew = (((index * 13 + seed) % 5) - 2) * 0.03
        const item = (
          <Slab
            key={index}
            tone={index % 3 === 1 ? 'accent' : 'shellSoft'}
            color={index % 3 === 1 ? accent : undefined}
            size={[width, depth, thickness]}
            radius={0.004}
            position={[skew * 0.4, y + thickness / 2, skew]}
            rotation={[0, skew, 0]}
          />
        )
        y += thickness
        return item
      })}
    </group>
  )
}

export function PaperStack({ base = 0, width = 0.21, depth = 0.297 }: { base?: number; width?: number; depth?: number }) {
  return (
    <group>
      <Slab tone="shell" size={[width, depth, 0.008]} radius={0.002} position={[0, base + 0.004, 0]} />
      <Slab tone="shell" size={[width, depth, 0.006]} radius={0.002} position={[0.004, base + 0.011, -0.005]} rotation={[0, 0.04, 0]} />
    </group>
  )
}

export function PaperTray({ base = 0, accent }: { base?: number; accent?: string }) {
  return (
    <group>
      <Box tone="darkSoft" size={[0.25, 0.012, 0.33]} position={[0, base + 0.006, 0]} />
      {[-1, 1].map((side) => (
        <Box key={side} tone="darkSoft" size={[0.012, 0.06, 0.33]} position={[side * 0.119, base + 0.036, 0]} />
      ))}
      <Slab tone="accent" color={accent} size={[0.21, 0.29, 0.01]} radius={0.003} position={[0, base + 0.017, 0]} />
    </group>
  )
}

export function Mug({ base = 0, tone = 'shell', accent }: { base?: number; tone?: Tone; accent?: string }) {
  return (
    <group>
      <Lathe
        tone={tone}
        color={accent}
        profileKey="mug"
        segments={20}
        profile={[
          [0, 0],
          [0.038, 0],
          [0.038, 0.002],
          [0.041, 0.092],
          [0.036, 0.092],
          [0.033, 0.008],
          [0, 0.008],
        ]}
        position={[0, base, 0]}
      />
      <Ring tone={tone} color={accent} r={0.024} tube={0.005} position={[0.052, base + 0.055, 0]} rotation={[0, Math.PI / 2, 0]} />
    </group>
  )
}

export function Notebook({ base = 0, accent }: { base?: number; accent?: string }) {
  return (
    <group>
      <Slab tone="accent" color={accent} size={[0.16, 0.22, 0.014]} radius={0.006} position={[0, base + 0.007, 0]} />
      <Cyl tone="metalDark" axis="x" r={0.006} h={0.15} segments={8} position={[0, base + 0.015, -0.104]} />
    </group>
  )
}

export interface DeskLampProps {
  base?: number
  tone?: Tone
  accent?: string
}

/** Articulated desk lamp — a strong silhouette element on an otherwise flat desk. */
export function DeskLamp({ base = 0, tone = 'darkSoft', accent }: DeskLampProps) {
  return (
    <group position={[0, base, 0]}>
      <Cyl tone={tone} r={0.075} h={0.014} position={[0, 0.007, 0]} segments={22} />
      <Bar tone={tone} r={0.009} from={[0, 0.012, 0]} to={[0.02, 0.34, -0.05]} />
      <Bar tone={tone} r={0.009} from={[0.02, 0.34, -0.05]} to={[0.02, 0.3, 0.16]} />
      <Lathe
        tone="accent"
        color={accent}
        profileKey="lampshade"
        segments={20}
        profile={[
          [0.02, 0.09],
          [0.062, 0],
          [0.058, -0.004],
          [0.018, 0.086],
        ]}
        position={[0.02, 0.26, 0.18]}
        rotation={[0.5, 0, 0]}
      />
    </group>
  )
}

export interface FramedArtProps {
  width?: number
  height?: number
  accent?: string
  frameTone?: Tone
  /** `matte` leaves a white border around the accent field. */
  matte?: boolean
}

/** Wall art. Origin at the centre of the frame, face towards +Z. */
export function FramedArt({ width = 0.44, height = 0.56, accent, frameTone = 'shell', matte = true }: FramedArtProps) {
  return (
    <group>
      <Slab tone={frameTone} size={[width, height, 0.026]} radius={0.006} rotation={[Math.PI / 2, 0, 0]} />
      <Slab
        tone="accent"
        color={accent}
        size={[width - (matte ? 0.12 : 0.03), height - (matte ? 0.14 : 0.03), 0.006]}
        radius={0.004}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, 0, 0.015]}
      />
    </group>
  )
}

export function WallClock({ r = 0.14, accent }: { r?: number; accent?: string }) {
  return (
    <group>
      <Cyl tone="shell" axis="z" r={r} h={0.035} segments={30} />
      <Cyl tone="screen" axis="z" r={r - 0.014} h={0.004} segments={30} position={[0, 0, 0.019]} />
      <Box tone="darkSoft" size={[0.008, r * 0.6, 0.004]} position={[0, r * 0.24, 0.022]} />
      <Box tone="accent" color={accent} size={[r * 0.5, 0.007, 0.004]} position={[r * 0.18, 0, 0.022]} />
    </group>
  )
}

export function Rug({
  width = 2,
  depth = 1.4,
  radius = 0.1,
  tone = 'shellSoft',
  accent,
}: {
  width?: number
  depth?: number
  radius?: number
  tone?: Tone
  accent?: string
}) {
  return (
    <group>
      <Slab tone={tone} size={[width, depth, 0.012]} radius={radius} position={[0, 0.006, 0]} />
      <Slab tone="accent" color={accent} size={[width - 0.18, depth - 0.18, 0.004]} radius={Math.max(radius - 0.06, 0.02)} position={[0, 0.014, 0]} />
    </group>
  )
}

export function WasteBin({ tone = 'shellSoft' }: { tone?: Tone }) {
  return (
    <Lathe
      tone={tone}
      profileKey="bin"
      segments={24}
      profile={[
        [0, 0],
        [0.11, 0],
        [0.135, 0.32],
        [0.135, 0.34],
        [0.125, 0.34],
        [0.125, 0.33],
        [0.1, 0.012],
        [0, 0.012],
      ]}
    />
  )
}

export function CoatStand({ tone = 'metalDark', accent }: { tone?: Tone; accent?: string }) {
  return (
    <group>
      <Cyl tone={tone} r={0.16} h={0.02} position={[0, 0.01, 0]} segments={24} />
      <Cyl tone={tone} r={0.022} h={1.68} position={[0, 0.86, 0]} segments={14} />
      {Array.from({ length: 4 }, (_, index) => {
        const angle = (index / 4) * Math.PI * 2 + 0.4
        return (
          <group key={index} rotation={[0, angle, 0]}>
            <Bar tone={tone} r={0.014} from={[0, 1.62, 0]} to={[0, 1.68, 0.14]} />
            <RBox tone="accent" color={accent} size={[0.03, 0.03, 0.03]} radius={0.012} position={[0, 1.69, 0.155]} />
          </group>
        )
      })}
    </group>
  )
}

export function Whiteboard({
  width = 1.6,
  height = 1.05,
  accent,
  onWall = true,
}: {
  width?: number
  height?: number
  accent?: string
  onWall?: boolean
}) {
  return (
    <group>
      <Slab tone="metal" size={[width, height, 0.03]} radius={0.008} rotation={[Math.PI / 2, 0, 0]} />
      <Slab tone="shell" size={[width - 0.05, height - 0.05, 0.008]} radius={0.004} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.018]} />
      <Slab tone="accent" color={accent} size={[width * 0.34, height * 0.22, 0.004]} radius={0.004} rotation={[Math.PI / 2, 0, 0]} position={[-width * 0.16, height * 0.14, 0.023]} />
      {onWall ? null : <Box tone="metalDark" size={[width * 0.5, 0.04, 0.08]} position={[0, -height / 2 - 0.02, 0.02]} />}
    </group>
  )
}
